import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_vueplausibleclient_5ec0e45c from 'nuxt_plugin_vueplausibleclient_5ec0e45c' // Source: ./vue-plausible.client.js (mode: 'client')
import nuxt_plugin_templatesplugin76e89ed0_c7548492 from 'nuxt_plugin_templatesplugin76e89ed0_c7548492' // Source: ./templates.plugin.76e89ed0.js (mode: 'client')
import nuxt_plugin_axios_73f39a51 from 'nuxt_plugin_axios_73f39a51' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_ThrowPropsPlugin_51559e3c from 'nuxt_plugin_ThrowPropsPlugin_51559e3c' // Source: ../plugins/GSAP/ThrowPropsPlugin.js (mode: 'client')
import nuxt_plugin_ScrollToPlugin_64394c7e from 'nuxt_plugin_ScrollToPlugin_64394c7e' // Source: ../plugins/GSAP/ScrollToPlugin.js (mode: 'client')
import nuxt_plugin_directives_d0867c0c from 'nuxt_plugin_directives_d0867c0c' // Source: ../plugins/directives (mode: 'client')
import nuxt_plugin_ksvuescrollmagic_03bbeed0 from 'nuxt_plugin_ksvuescrollmagic_03bbeed0' // Source: ../plugins/ksvuescrollmagic (mode: 'client')
import nuxt_plugin_vueMapBox_ade91582 from 'nuxt_plugin_vueMapBox_ade91582' // Source: ../plugins/vueMapBox (mode: 'client')
import nuxt_plugin_lottie_52d78ad6 from 'nuxt_plugin_lottie_52d78ad6' // Source: ../plugins/lottie (mode: 'client')
import nuxt_plugin_axios_3566aa80 from 'nuxt_plugin_axios_3566aa80' // Source: ../plugins/axios (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"htmlAttrs":{"lang":"en"},"title":"&Tradition","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1, user-scalable=yes"},{"name":"facebook-domain-verification","content":"ati6lvdo8ph960pfpp3uj4uz99ydlg"},{"hid":"og:locale","property":"og:locale","content":"en-GB"},{"hid":"description","name":"description","content":"&Tradition"}],"script":[{"src":"https:\u002F\u002Fconsent.cookiebot.com\u002Fuc.js","id":"Cookiebot","data-cbid":"b8377a3d-d11f-458f-93fc-0a55cb736fa0","rel":"preconnect"},{"src":"https:\u002F\u002Fstatic.klaviyo.com\u002Fonsite\u002Fjs\u002Fklaviyo.js?company_id=Sbfn89","defer":true,"async":true,"rel":"preconnect"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.png"},{"rel":"stylesheet","href":"https:\u002F\u002Fuse.typekit.net\u002Firs0yhv.css"},{"rel":"stylesheet","href":"https:\u002F\u002Fuse.typekit.net\u002Fdcf4bgx.css"},{"rel":"preload","href":"https:\u002F\u002Fviewer.cylindo.com\u002Fv4\u002Fviewer.min.css","as":"style","onload":"this.onload=null;this.rel='stylesheet'"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.client && typeof nuxt_plugin_vueplausibleclient_5ec0e45c === 'function') {
    await nuxt_plugin_vueplausibleclient_5ec0e45c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_templatesplugin76e89ed0_c7548492 === 'function') {
    await nuxt_plugin_templatesplugin76e89ed0_c7548492(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_73f39a51 === 'function') {
    await nuxt_plugin_axios_73f39a51(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ThrowPropsPlugin_51559e3c === 'function') {
    await nuxt_plugin_ThrowPropsPlugin_51559e3c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ScrollToPlugin_64394c7e === 'function') {
    await nuxt_plugin_ScrollToPlugin_64394c7e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_directives_d0867c0c === 'function') {
    await nuxt_plugin_directives_d0867c0c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ksvuescrollmagic_03bbeed0 === 'function') {
    await nuxt_plugin_ksvuescrollmagic_03bbeed0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueMapBox_ade91582 === 'function') {
    await nuxt_plugin_vueMapBox_ade91582(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_lottie_52d78ad6 === 'function') {
    await nuxt_plugin_lottie_52d78ad6(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_3566aa80 === 'function') {
    await nuxt_plugin_axios_3566aa80(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
